import { useMemo } from 'react';
import cn from 'classnames';
import { CreativeInfoItem } from '@blisspointmedia/bpm-types/dist/Creative';
import { ChartContainer } from '@Components/ChartContainer';
import { Dropdown } from '@Components/Dropdown';
import { Badge } from '@Components/Badge';
import { Design, StretchMode } from '@Components/designConstants';
import { EmptyState } from '@Components/EmptyState';
import { Img } from '@Components/Img';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { getCreativeThumbnail } from '@Components/MetricsTable/metrics-table-utils';
import { BsPlayBtn } from 'react-icons/bs';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { CreativeInsightsData, Selection } from './creative-insights-constants';
import { creativeMatchesTag, getCreativeKey } from './creative-insights-utils';
import { CreativeView } from './CreativeView';
import { SelectionActions } from './use-creative-insights';
import './CreativeShelf.scss';

const getPlaceholderMessage = ({ attribute, tag }: Selection): string => {
  if (!attribute) {
    return 'First select an attribute, then select a tactic to view creatives.';
  }
  if (!tag) {
    return 'Select a tactic to view creatives.';
  }
  return 'No creatives available for the selected attribute and tactic.';
};

interface CreativeShelfProps {
  data: CreativeInsightsData;
  selection: Selection;
  selectedCreative: CreativeInfoItem | undefined;
  actions: SelectionActions;
}

export const CreativeShelf = ({
  data: { attributeTypeBinomial, tagImportanceData, creativeInfoList },
  selection,
  selectedCreative,
  actions: { selectTag, clearTag, selectCreative }
}: CreativeShelfProps) => {
  const { cid } = useCompanyInfo();
  const { attribute: selectedAttribute, tag: selectedTag, creativeKey } = selection;

  const filteredCreativeList = useMemo(
    () =>
      creativeInfoList
        .filter(creative =>
          creativeMatchesTag(creative, selectedAttribute, selectedTag, attributeTypeBinomial)
        )
        .sort((creativeA, creativeB) => creativeA.name.localeCompare(creativeB.name)),
    [creativeInfoList, selectedAttribute, selectedTag, attributeTypeBinomial]
  );

  const tagDropDownOptions = tagImportanceData[selectedAttribute]?.map(tag => tag.name) ?? [];

  return (
    <ChartContainer
      className='creative-shelf'
      title='Creatives'
      viewRightActions
      rightActions={
        <>
          <Badge
            design={Design.NEUTRAL}
          >{`Attribute: ${selectedAttribute || 'Select Attribute'}`}</Badge>
          <Dropdown
            disabled={!selectedAttribute}
            stretch={StretchMode.FIT}
            value={selectedTag}
            options={tagDropDownOptions}
            onChange={selectTag}
            clearable
            onClear={clearTag}
            label='Tactic'
            placeholder='Select Tactic'
          />
        </>
      }
    >
      <div className='creatives-content'>
        {filteredCreativeList.length > 0 ? (
          <div className='content'>
            <CreativeView cid={cid} selectedCreative={selectedCreative} />
            <div className='creatives-list' data-testid='creatives-list'>
              {filteredCreativeList.map(creative => (
                <CreativeShelfItem
                  key={getCreativeKey(creative)}
                  cid={cid}
                  creative={creative}
                  active={creativeKey === getCreativeKey(creative)}
                  onSelect={selectCreative}
                />
              ))}
            </div>
          </div>
        ) : (
          <EmptyState
            className='creative-list-placeholder'
            icon={<BsPlayBtn size={100} />}
            description={getPlaceholderMessage(selection)}
          />
        )}
      </div>
    </ChartContainer>
  );
};

interface CreativeShelfItemProps {
  cid: string;
  creative: CreativeInfoItem;
  active: boolean;
  onSelect: (creativeKey: string) => void;
}

const CreativeShelfItem = ({ cid, creative, active, onSelect }: CreativeShelfItemProps) => (
  <InteractiveDiv
    title={creative.name}
    className={cn('creative-row-item', { active })}
    data-testid='creative-item'
    onClick={() => onSelect(getCreativeKey(creative))}
  >
    <div className='thumbnail-container'>
      <Img
        className='video-thumbnail'
        src={getCreativeThumbnail(cid, creative.file)}
        unloader={
          <div className='video-thumbnail'>
            <BsPlayBtn size={100} />
          </div>
        }
      />
    </div>
    <div className='descriptions'>
      <span className='title'>{`${creative.name} (${creative.length})`}</span>
    </div>
  </InteractiveDiv>
);
